/**
 * useTrackerClientOrg — resolve the cross-org client chip for tracker rows.
 *
 * BLU-367: at a shared 3PL location the tracker lists orders belonging to
 * several client orgs. Every order-level row shows whose order it is; own-org
 * rows show nothing (a chip on every row is noise, not information).
 *
 * Why this exists rather than `useClientOrgLabels`: that hook resolves names
 * from `locations.listConnectedClients`, which is an **adminProcedure** — it
 * returns no data for CX and Warehouse Coordinators, who are a large part of
 * the tracker's audience, leaving them a generic "Client" badge on every
 * cross-org row. This one reads the location-scoped
 * `listConnectedClientsAtLocation` (a protectedProcedure) via the shared
 * `useConnectedClients` wrapper, so non-admins see real client names.
 *
 * Location + org come from context, so leaf row components can call this
 * directly instead of threading a resolver down through the batch table's
 * three nesting levels. React Query dedupes against the page's own
 * `useClientOrgFilter` call — same query key, one request.
 */
import { useCallback, useMemo } from 'react';

import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useConnectedClients } from '@/features/inventory/hooks/useConnectedClients';

export interface TrackerClientOrg {
  /** True only for a genuine non-caller org — the gate for rendering a chip. */
  isCrossOrg: (orgId: string | null | undefined) => boolean;
  /**
   * Client display name, or undefined when unknown. Undefined still renders a
   * chip (the row IS cross-org) with `ClientOrgChip`'s generic fallback — the
   * row is never silently attributed to the wrong org.
   */
  labelFor: (orgId: string | null | undefined) => string | undefined;
}

export function useTrackerClientOrg(): TrackerClientOrg {
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const sharedLocationsEnabled =
    (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? '') === 'true';

  const { clients } = useConnectedClients({
    locationId: selectedLocation?.id,
    enabled: sharedLocationsEnabled,
  });

  const callerOrgId = organization?.id;

  const nameById = useMemo(
    () => new Map(clients.map((c) => [c.id, c.name])),
    [clients],
  );

  const isCrossOrg = useCallback(
    (orgId: string | null | undefined) =>
      !!callerOrgId && !!orgId && orgId !== callerOrgId,
    [callerOrgId],
  );

  const labelFor = useCallback(
    (orgId: string | null | undefined) => {
      if (!orgId || orgId === callerOrgId) return undefined;
      return nameById.get(orgId);
    },
    [callerOrgId, nameById],
  );

  return { isCrossOrg, labelFor };
}
